import { TodoItem } from "./adhd-todo-manager";

export interface TaskTemplate {
  id: string;
  name: string;
  description: string;
  category: "routine" | "project" | "health" | "work" | "personal" | "creative";
  icon: string;
  estimatedTime?: string;
  difficulty?: "easy" | "medium" | "hard";
  tags: string[];
  tasks: Array<{
    title: string;
    description?: string;
    priority?: "low" | "medium" | "high" | "urgent";
    estimatedTime?: number; // minutes
    tags?: string[];
    subtasks?: Array<{
      title: string;
      estimatedTime?: number;
    }>;
  }>;
}

export const TASK_TEMPLATES: TaskTemplate[] = [
  // Morning Routines
  {
    id: "morning-routine-basic",
    name: "Basic Morning Routine",
    description: "A simple, ADHD-friendly morning routine to start your day right",
    category: "routine",
    icon: "🌅",
    estimatedTime: "45-60 minutes",
    difficulty: "easy",
    tags: ["morning", "routine", "self-care", "energy"],
    tasks: [
      {
        title: "Wake up and get out of bed",
        description: "Set multiple alarms if needed. Use a sunrise alarm clock.",
        priority: "high",
        estimatedTime: 5,
        tags: ["wake-up"],
      },
      {
        title: "Drink a glass of water",
        description: "Rehydrate after sleeping. Keep water by your bed.",
        priority: "high",
        estimatedTime: 2,
        tags: ["hydration", "health"],
      },
      {
        title: "Take morning medication (if applicable)",
        description: "Set up a pill organizer or medication reminder app",
        priority: "urgent",
        estimatedTime: 3,
        tags: ["medication", "health"],
      },
      {
        title: "Quick bathroom routine",
        description: "Use the bathroom, wash hands and face, brush teeth",
        priority: "high",
        estimatedTime: 10,
        tags: ["hygiene"],
        subtasks: [
          { title: "Use bathroom", estimatedTime: 3 },
          { title: "Wash hands and face", estimatedTime: 4 },
          { title: "Brush teeth", estimatedTime: 3 },
        ],
      },
      {
        title: "Get dressed",
        description: "Lay out clothes the night before to reduce decision fatigue",
        priority: "medium",
        estimatedTime: 10,
        tags: ["clothing", "preparation"],
      },
      {
        title: "Eat breakfast",
        description: "Something with protein to fuel your brain. Prep options ahead.",
        priority: "high",
        estimatedTime: 15,
        tags: ["nutrition", "health"],
      },
      {
        title: "Review daily priorities",
        description: "Check your calendar and top 3 tasks for the day",
        priority: "medium",
        estimatedTime: 5,
        tags: ["planning", "organization"],
      },
    ],
  },

  // Work Projects
  {
    id: "work-project-setup",
    name: "New Work Project Setup",
    description: "Essential steps to organize and plan any new work project",
    category: "work",
    icon: "💼",
    estimatedTime: "2-3 hours",
    difficulty: "medium",
    tags: ["work", "project", "organization", "planning"],
    tasks: [
      {
        title: "Define project scope and goals",
        description: "Write down what success looks like. Be specific and measurable.",
        priority: "urgent",
        estimatedTime: 30,
        tags: ["planning", "goals"],
      },
      {
        title: "Identify key stakeholders",
        description: "List everyone who needs to be involved or informed",
        priority: "high",
        estimatedTime: 15,
        tags: ["stakeholders", "communication"],
      },
      {
        title: "Break down into major milestones",
        description: "Divide the project into 3-5 major checkpoints",
        priority: "high",
        estimatedTime: 45,
        tags: ["milestones", "planning"],
        subtasks: [
          { title: "List major deliverables", estimatedTime: 20 },
          { title: "Set milestone dates", estimatedTime: 15 },
          { title: "Identify dependencies", estimatedTime: 10 },
        ],
      },
      {
        title: "Create initial task breakdown",
        description: "Break each milestone into actionable tasks (can refine later)",
        priority: "medium",
        estimatedTime: 60,
        tags: ["tasks", "breakdown"],
      },
      {
        title: "Set up project workspace",
        description: "Create folders, documents, and organization system",
        priority: "medium",
        estimatedTime: 20,
        tags: ["workspace", "organization"],
      },
      {
        title: "Schedule kickoff meeting",
        description: "Get everyone aligned on goals, timeline, and expectations",
        priority: "high",
        estimatedTime: 15,
        tags: ["meetings", "communication"],
      },
    ],
  },

  // Health & Wellness
  {
    id: "adhd-wellness-routine",
    name: "ADHD Wellness Check-in",
    description: "Daily wellness routine specifically designed for ADHD management",
    category: "health",
    icon: "🧠",
    estimatedTime: "30-45 minutes",
    difficulty: "easy",
    tags: ["adhd", "wellness", "mental-health", "routine"],
    tasks: [
      {
        title: "Rate current energy level (1-10)",
        description: "Track your energy patterns to optimize your day",
        priority: "medium",
        estimatedTime: 2,
        tags: ["energy", "tracking"],
      },
      {
        title: "Check in with emotions",
        description: "Notice what you're feeling without judgment. Name 2-3 emotions.",
        priority: "medium",
        estimatedTime: 5,
        tags: ["emotions", "mindfulness"],
      },
      {
        title: "5-minute mindfulness exercise",
        description: "Deep breathing, brief meditation, or mindful observation",
        priority: "medium",
        estimatedTime: 5,
        tags: ["mindfulness", "stress-relief"],
        subtasks: [
          { title: "10 deep breaths", estimatedTime: 2 },
          { title: "Body scan check-in", estimatedTime: 3 },
        ],
      },
      {
        title: "Move your body",
        description: "Any movement counts - stretching, walk, dancing, exercise",
        priority: "high",
        estimatedTime: 15,
        tags: ["exercise", "movement"],
      },
      {
        title: "Hydration check",
        description: "Have you had enough water today? Drink a glass now.",
        priority: "high",
        estimatedTime: 3,
        tags: ["hydration", "health"],
      },
      {
        title: "Celebrate one win from today",
        description: "Acknowledge something you accomplished, however small",
        priority: "low",
        estimatedTime: 3,
        tags: ["gratitude", "positivity"],
      },
      {
        title: "Plan for tomorrow's success",
        description: "Set out clothes, prep breakfast, or choose your top 3 priorities",
        priority: "medium",
        estimatedTime: 10,
        tags: ["preparation", "planning"],
      },
    ],
  },

  // Creative Projects
  {
    id: "creative-project-start",
    name: "Creative Project Kickstart",
    description: "Get unstuck and start any creative project with ADHD-friendly structure",
    category: "creative",
    icon: "🎨",
    estimatedTime: "1-2 hours",
    difficulty: "medium",
    tags: ["creative", "project", "inspiration", "structure"],
    tasks: [
      {
        title: "Set a timer for 25 minutes (Pomodoro)",
        description: "Use time pressure to overcome perfectionism and get started",
        priority: "high",
        estimatedTime: 1,
        tags: ["timer", "pomodoro"],
      },
      {
        title: "Brain dump all ideas",
        description: "Write down every idea, no filtering. Quantity over quality.",
        priority: "high",
        estimatedTime: 15,
        tags: ["brainstorming", "ideas"],
      },
      {
        title: "Choose the most exciting element",
        description: "What part makes you want to start right now? Start there.",
        priority: "medium",
        estimatedTime: 5,
        tags: ["decision-making", "excitement"],
      },
      {
        title: 'Create a "good enough" first version',
        description: "Make something terrible but complete. You can improve it later.",
        priority: "high",
        estimatedTime: 45,
        tags: ["prototype", "progress"],
      },
      {
        title: "Take a break and celebrate",
        description: "You started! That's the hardest part. Rest and acknowledge this win.",
        priority: "medium",
        estimatedTime: 10,
        tags: ["celebration", "rest"],
      },
      {
        title: "Plan next small step",
        description: "What's one tiny improvement you could make next time?",
        priority: "low",
        estimatedTime: 5,
        tags: ["planning", "next-step"],
      },
    ],
  },

  // Personal Organization
  {
    id: "personal-organization-reset",
    name: "Personal Space Organization",
    description: "Quick organization routine for overwhelmed ADHD brains",
    category: "personal",
    icon: "🧹",
    estimatedTime: "1-2 hours",
    difficulty: "easy",
    tags: ["organization", "decluttering", "environment", "reset"],
    tasks: [
      {
        title: "Choose ONE small area to focus on",
        description: "Desk, kitchen counter, or bedroom floor. Start small!",
        priority: "high",
        estimatedTime: 5,
        tags: ["focus", "scope"],
      },
      {
        title: "Set a timer for 15 minutes",
        description: "Short bursts prevent overwhelm and maintain motivation",
        priority: "high",
        estimatedTime: 1,
        tags: ["timer", "pacing"],
      },
      {
        title: "Sort items into 4 piles",
        description: "Keep here, Put away elsewhere, Donate/give away, Trash",
        priority: "high",
        estimatedTime: 15,
        tags: ["sorting", "decluttering"],
        subtasks: [
          { title: "Keep here pile", estimatedTime: 5 },
          { title: "Put away elsewhere pile", estimatedTime: 5 },
          { title: "Donate/give away pile", estimatedTime: 3 },
          { title: "Trash pile", estimatedTime: 2 },
        ],
      },
      {
        title: 'Deal with "Keep here" items',
        description: "Find a home for everything. Use containers or labels if needed.",
        priority: "medium",
        estimatedTime: 20,
        tags: ["organization", "storage"],
      },
      {
        title: "Put away items that belong elsewhere",
        description: "Return items to their proper homes throughout your space",
        priority: "medium",
        estimatedTime: 15,
        tags: ["tidying", "organizing"],
      },
      {
        title: "Take out trash and set aside donations",
        description: "Remove items that are leaving. Put donations in your car!",
        priority: "medium",
        estimatedTime: 10,
        tags: ["removal", "donations"],
      },
      {
        title: "Take an after photo and celebrate",
        description: "Document your progress! You did great work.",
        priority: "low",
        estimatedTime: 2,
        tags: ["celebration", "documentation"],
      },
    ],
  },

  // Work Day Productivity
  {
    id: "productive-work-day",
    name: "Productive Work Day Structure",
    description: "ADHD-friendly framework for a focused and productive workday",
    category: "work",
    icon: "⚡",
    estimatedTime: "Full day",
    difficulty: "medium",
    tags: ["work", "productivity", "structure", "focus"],
    tasks: [
      {
        title: "Start with hardest task (Eat the Frog)",
        description: "Tackle your most challenging or important task first while energy is high",
        priority: "urgent",
        estimatedTime: 90,
        tags: ["priority", "difficulty"],
      },
      {
        title: "Take a 15-minute break",
        description: "Step away from your workspace. Move your body or rest your eyes.",
        priority: "high",
        estimatedTime: 15,
        tags: ["break", "recovery"],
      },
      {
        title: "Handle 2-3 quick administrative tasks",
        description: "Emails, scheduling, or paperwork while momentum is still good",
        priority: "medium",
        estimatedTime: 45,
        tags: ["admin", "quick-tasks"],
      },
      {
        title: "Focused work block (Pomodoro style)",
        description: "25 minutes focused work + 5 minute break, repeat 2-3 times",
        priority: "high",
        estimatedTime: 90,
        tags: ["focus", "pomodoro"],
      },
      {
        title: "Lunch break with mindful eating",
        description: "Step away from work completely. Eat away from your desk if possible.",
        priority: "high",
        estimatedTime: 45,
        tags: ["lunch", "mindfulness"],
      },
      {
        title: "Review and adjust priorities",
        description: "Check your progress and adjust afternoon goals if needed",
        priority: "medium",
        estimatedTime: 10,
        tags: ["review", "adjustment"],
      },
      {
        title: "Creative or collaborative work",
        description: "Afternoon energy often better for collaboration and creative tasks",
        priority: "medium",
        estimatedTime: 90,
        tags: ["creative", "collaboration"],
      },
      {
        title: "End-of-day wrap-up",
        description: "Review accomplishments, plan tomorrow, clear your workspace",
        priority: "medium",
        estimatedTime: 15,
        tags: ["wrap-up", "planning"],
      },
    ],
  },
];

// Helper functions for working with templates
export const getTemplatesByCategory = (category: TaskTemplate["category"]): TaskTemplate[] => {
  return TASK_TEMPLATES.filter(template => template.category === category);
};

export const getTemplateById = (id: string): TaskTemplate | undefined => {
  return TASK_TEMPLATES.find(template => template.id === id);
};

export const getTemplatesByDifficulty = (
  difficulty: TaskTemplate["difficulty"]
): TaskTemplate[] => {
  return TASK_TEMPLATES.filter(template => template.difficulty === difficulty);
};

export const searchTemplates = (query: string): TaskTemplate[] => {
  const lowercaseQuery = query.toLowerCase();
  return TASK_TEMPLATES.filter(
    template =>
      template.name.toLowerCase().includes(lowercaseQuery) ||
      template.description.toLowerCase().includes(lowercaseQuery) ||
      template.tags.some(tag => tag.toLowerCase().includes(lowercaseQuery))
  );
};

// Convert a template to actual TodoItems
export const convertTemplateToTodos = (
  template: TaskTemplate,
  projectId?: string
): Omit<TodoItem, "id" | "status">[] => {
  const now = new Date().toISOString();

  return template.tasks.map((task, index) => ({
    title: task.title,
    description: task.description || "",
    priority: task.priority || "medium",
    estimated_minutes: task.estimatedTime || 30,
    tags: [...(task.tags || []), ...template.tags],
    project_id: projectId || undefined,
    due_date: undefined,
    notes: "",
    created_at: now,
  }));
};

export const getTemplateCategories = (): Array<{
  key: TaskTemplate["category"];
  label: string;
  icon: string;
  count: number;
}> => {
  const categories = {
    routine: { label: "Daily Routines", icon: "🔄" },
    work: { label: "Work & Professional", icon: "💼" },
    health: { label: "Health & Wellness", icon: "🌱" },
    creative: { label: "Creative Projects", icon: "🎨" },
    personal: { label: "Personal Organization", icon: "🏠" },
    project: { label: "Project Management", icon: "📋" },
  };

  return Object.entries(categories).map(([key, info]) => ({
    key: key as TaskTemplate["category"],
    label: info.label,
    icon: info.icon,
    count: getTemplatesByCategory(key as TaskTemplate["category"]).length,
  }));
};
